import { useEffect, useState } from 'react';
import {
	type AnalyticsEventPayload,
	type UIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlaskit/analytics-next';
import { fetchEntitlementsFromSearch } from '@atlassian/commerce-entitlements';
import { useCommerceFetch } from '@atlassian/commerce-environment';
import type { ProjectType } from '@atlassian/jira-common-constants';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	type ApplicationKey,
	type ApplicationEdition,
	STANDARD_EDITION,
	FREE_EDITION,
} from '@atlassian/jira-shared-types';
import {
	API_FAIL,
	API_SUCCESS,
	SEND_OPERATIONAL_EVENT,
	FETCH_SITE_UPGRADED_API_CALL,
} from '../constants';
import { getOfferingProductKey } from '../utils';
import { fetchSiteEdition } from './fetch-site-edition';

type Props = {
	currentEdition: ApplicationEdition;
	product: ApplicationKey;
	shouldCheckImmediately?: boolean;
};

const sendSuccessAnalytics = (
	createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent,
	currentEdition: string,
) => {
	const analyticsSetSuccessEvent = createAnalyticsEvent({
		type: SEND_OPERATIONAL_EVENT,
		action: API_SUCCESS,
		actionSubject: FETCH_SITE_UPGRADED_API_CALL,
	});
	fireOperationalAnalytics(
		analyticsSetSuccessEvent,
		`${FETCH_SITE_UPGRADED_API_CALL} ${API_SUCCESS}`,
		{
			currentEdition,
		},
	);
};

const sendErrorAnalytics = (
	err: Error,
	createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent,
) => {
	const analyticsSetFailEvent = createAnalyticsEvent({
		type: SEND_OPERATIONAL_EVENT,
		action: API_FAIL,
		actionSubject: FETCH_SITE_UPGRADED_API_CALL,
	});
	fireOperationalAnalytics(analyticsSetFailEvent, `${FETCH_SITE_UPGRADED_API_CALL} ${API_FAIL}`, {
		errorMessage: err.message,
	});
};

/**
 * Hook that polls to check if the site has been upgraded from free edition
 *
 * @param currentEdition
 * @param product
 * @returns boolean
 */
export const useHasBeenUpgradedFromFree = ({
	currentEdition,
	product,
	shouldCheckImmediately = false,
}: Props): boolean => {
	const [hasBeenUpgradedFromFree, setHasBeenUpgradedFromFree] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		let intervalId: NodeJS.Timeout;
		if (currentEdition === FREE_EDITION) {
			const fetchUpdatedEdition = () => {
				// if upgraded then clear the interval
				fetchSiteEdition(product)
					.then((siteEdition) => {
						sendSuccessAnalytics(createAnalyticsEvent, siteEdition);
						setHasBeenUpgradedFromFree(siteEdition === STANDARD_EDITION);
						if (siteEdition !== FREE_EDITION) {
							clearInterval(intervalId);
						}
					})
					.catch((err) => {
						sendErrorAnalytics(err, createAnalyticsEvent);
						clearInterval(intervalId);
					});
			};

			intervalId = setInterval(fetchUpdatedEdition, 1000 * 60 * 5);

			shouldCheckImmediately && fetchUpdatedEdition();
		}

		return () => clearInterval(intervalId);
	}, [createAnalyticsEvent, currentEdition, product, shouldCheckImmediately]);

	return hasBeenUpgradedFromFree;
};

export const useHasMultipleCCPProducts = (
	projectType: ProjectType,
	shouldRun: boolean,
	transactionAccountId?: string | null,
) => {
	const [hasMultiple, setHasMultiple] = useState<boolean | null>(null);
	const fetch = useCommerceFetch();

	useEffect(() => {
		if (!shouldRun) {
			return;
		}
		if (!transactionAccountId) {
			setHasMultiple(false);
			return;
		}
		(async () => {
			const entitlements = await fetchEntitlementsFromSearch(fetch, transactionAccountId);
			const productName = getOfferingProductKey(projectType);

			const matchingProducts = entitlements
				.map(({ offering }) => offering.product.name)
				.filter((name) => name === productName);

			setHasMultiple(matchingProducts.length > 1);
		})();
	}, [fetch, projectType, shouldRun, transactionAccountId]);

	return {
		isLoading: hasMultiple === null,
		hasMultiple,
	};
};
